/**
 * Server state for the notifications feed (HS-1443, moved onto the Admin API by HS-1504, shared by
 * the portal and admin since HS-1548).
 *
 * **The only file in this feature that names a transport.** HS-1443 built the screen against the
 * mock and confined the transport to this module; HS-1504 spent that: the four operations became
 * generated client calls and `toApiError` learned to read `NormalizedApiError`. Nothing else in the
 * feature moved — not the adapter, not a component, not a test of either.
 *
 * Three rules this module owns:
 *
 * - **One unread number.** The header badge and the sidebar badge both read
 *   {@link notificationsKeys.unreadCount}. Every list load writes its `unreadCount` there, so a
 *   loaded list and the badge cannot disagree (contract §4).
 * - **Every write refreshes the whole resource**: the list under every filter, the count, and any
 *   query of the app's own that reads the same `/api/portal/me/notifications` resource — passed in
 *   as `invalidate` (the portal's dashboard delivery feed). Until HS-1504 that feed was live while
 *   this list was mock, and the two described different rows.
 *
 * The keys carry no app prefix (HS-1548): the package serves the portal and admin, and each app
 * has its own `QueryClient`.
 * - **Optimistic, with a real rollback.** The row turns read at once; a failure puts every loaded
 *   page and the count back exactly as they were.
 *
 * The writes stay hand-built rather than composed through `@smartsash/server-ops`: that package
 * pairs generated mutation hooks with the invalidation registry, and the optimistic read-and-
 * rollback below is specific to this feed. Moving it there is a refactor, not part of the swap.
 */
import {
  getApiPortalMeNotifications,
  getApiPortalMeNotificationsUnreadCount,
  NormalizedApiError,
  postApiPortalMeNotificationsIdRead,
  postApiPortalMeNotificationsReadAll,
  type GetApiPortalMeNotificationsParams,
  type PortalNotificationDocumentDto,
  type PortalNotificationDto,
  type PortalNotificationFeedDto,
  type PortalNotificationLinkDto,
  type PortalNotificationMessageDto,
  type PortalNotificationMessageDtoParams,
} from '@smartsash/api-client';
import {
  infiniteQueryOptions,
  queryOptions,
  type InfiniteData,
  type QueryClient,
  type QueryKey,
  type UseMutationOptions,
} from '@tanstack/react-query';

import { toNotificationPageView, toNotificationView } from './notifications.adapter';
import type {
  NotificationDocumentDto,
  NotificationItemDto,
  NotificationLinkDto,
  NotificationListParams,
  NotificationMessageDto,
  NotificationPageDto,
  NotificationPageView,
  NotificationParamValueDto,
  NotificationView,
} from './notifications.types';

/** Rows per load (contract §2 default). */
export const NOTIFICATIONS_PAGE_SIZE = 20;

/**
 * How long the count stays fresh for an ordinary mount — moving between screens does not re-read a
 * badge that was correct a moment ago. It does **not** govern the focus path; see below.
 */
export const UNREAD_COUNT_STALE_MS = 30_000;

/**
 * **The badge** repaints whenever the tab regains focus, whatever its age — and the feed does not.
 *
 * This overrides the app's `QueryClient` — the portal's sets `staleTime: 30_000` and leaves
 * `refetchOnWindowFocus` at its default `true`, under which a focus refetch fires only once the data
 * is already stale. That default is right for a list nobody else writes to. It is wrong for this
 * number: the feed is written from the dashboard's toasts and modals, from another device, and by
 * the backend itself, so returning to the tab five seconds after any of that must not show a stale
 * badge. Re-reading on focus is also the whole of the freshness story here, because subscribing to
 * `NotificationCreatedEvent` on the gRPC-web channel is out of scope (HS-1504).
 *
 * **Why the feed is deliberately left on the inherited `true`.** Refetching an infinite query
 * re-fetches *every loaded page, sequentially* — TanStack Query says so outright, and `maxPages`,
 * the one lever against it, needs `getPreviousPageParam`, which this contract cannot supply because
 * it issues a forward cursor only. So `'always'` on the feed would cost a user who has scrolled to
 * the tenth page ten sequential requests on every alt-tab, unconditionally. The inherited `true`
 * still repaints the list after any realistic absence; it only declines to do so for a five-second
 * one. The badge is what acceptance criterion 5 actually names, and it is one cheap request.
 *
 * There is a second reason not to re-read the pages more often than necessary: each page is
 * re-fetched with its stored keyset cursor, and marking rows read under `status=unread` moves the
 * page boundaries underneath it, so a re-read can skip or repeat a row. That is a property of
 * cursor paging rather than of this option, but frequency is what turns it from rare into visible.
 *
 * `'always'` rather than `staleTime: 0`, which would also re-read on every mount from the sidebar.
 */
const REFETCH_COUNT_ON_FOCUS = 'always' as const;

/** The list's filters — everything in the request except the page position. */
export type NotificationFilters = Omit<NotificationListParams, 'cursor' | 'limit'>;

export type NotificationPages = InfiniteData<NotificationPageView, string | null>;

export const notificationsKeys = {
  root: ['notifications'] as const,
  lists: () => ['notifications', 'list'] as const,
  list: (filters: NotificationFilters) => ['notifications', 'list', filters] as const,
  unreadCount: () => ['notifications', 'unreadCount'] as const,
};

/* ------------------------------------------------------------------ *
 * Errors
 * ------------------------------------------------------------------ */

const HTTP_NOT_FOUND = 404;

export type NotificationsApiReason = 'notFound' | 'unknown';

/** What `status` reads as when the request never reached a server at all. */
const NO_STATUS = 0;

/**
 * What a component gets when a read or a write fails. It exists so the three call sites branch on
 * one flat `reason` instead of importing the API-client package for a status comparison.
 */
export class NotificationsApiError extends Error {
  constructor(
    readonly status: number,
    readonly reason: NotificationsApiReason,
  ) {
    super(`Notifications API ${String(status)} (${reason})`);
    this.name = 'NotificationsApiError';
  }
}

/**
 * Every failed request arrives as a `NormalizedApiError`, whatever went wrong: the HTTP layer maps
 * an axios failure, a `{ success: false }` envelope and a timeout into that one shape.
 *
 * Only the 404 is named. It is the one failure the screen answers differently — the notification is
 * gone from the caller's feed, so the row is refreshed away rather than retried — and the contract
 * gives an unknown id, someone else's id and a malformed UUID the same 404, which is what keeps the
 * existence of another user's row unobservable. A missing status means the request never reached
 * the server; that is not a notFound, and it falls to `unknown` with the same treatment as a 500.
 */
export function toApiError(error: unknown): Error {
  if (error instanceof NotificationsApiError) return error;
  if (error instanceof NormalizedApiError) {
    // A network failure carries no status. Reporting it as 500 would put a server error in the log
    // for a request no server ever saw, so it keeps {@link NO_STATUS} and the same `unknown`
    // handling — the screen treats both the same way, but a reader can tell them apart.
    const status = error.status ?? NO_STATUS;
    return new NotificationsApiError(status, status === HTTP_NOT_FOUND ? 'notFound' : 'unknown');
  }
  return error instanceof Error ? error : new Error(String(error));
}

/** A read that failed because the notification is gone — the row is refreshed away, not retried. */
export function isNotFound(error: unknown): boolean {
  return error instanceof NotificationsApiError && error.reason === 'notFound';
}

/* ------------------------------------------------------------------ *
 * Generated client → our DTOs
 * ------------------------------------------------------------------ */

/**
 * The API's schemas mark **every** string `nullable: true` — a document-wide setting the backend
 * scoped out of its `required` fix — so an identifier the contract guarantees still generates as
 * `string | null`. A null there is a contract violation, not a value, and naming the field beats
 * an empty chip three layers down. Deliberately a local copy of the portal `dashboard.api.ts`'s
 * helper rather than a shared export: six lines are cheaper than turning one feature's private
 * guard into another package's public API.
 */
function text(value: string | null | undefined, field: string): string {
  if (value === null || value === undefined) {
    throw new Error(`Admin API sent null for "${field}", which the contract guarantees`);
  }
  return value;
}

/**
 * `params` arrives as free-form JSON, so a value can be anything. The copy interpolates strings and
 * numbers; anything else — a boolean, an object, an explicit null — has no spelling in a sentence
 * and is dropped rather than rendered as `[object Object]`. A missing parameter leaves the copy's
 * placeholder visible, which reads as a bug and is therefore better than silently plausible prose.
 */
function toParamsDto(
  // The field is optional *and* its type is independently nullable, so both absences arrive here.
  params: PortalNotificationMessageDtoParams | undefined,
): Record<string, NotificationParamValueDto> {
  const view: Record<string, NotificationParamValueDto> = {};
  for (const [name, value] of Object.entries(params ?? {})) {
    if (typeof value === 'string' || typeof value === 'number') view[name] = value;
  }
  return view;
}

/**
 * The wire carries one flat object with both halves present and one of them nulled; the adapter
 * expects a discriminated union. Only `type: "text"` takes the verbatim branch — anything else,
 * including a `type` this release does not know, becomes a `key` message, because an unrecognised
 * `titleKey` already renders as the generic "You have a new notification" rather than as a raw
 * path. That is the graceful failure; treating an unknown `type` as text would print `null`.
 */
function toMessageDto(message: PortalNotificationMessageDto): NotificationMessageDto {
  if (message.type === 'text') {
    return { type: 'text', title: message.title ?? '', body: message.body ?? '' };
  }
  return { type: 'key', titleKey: message.titleKey ?? '', params: toParamsDto(message.params) };
}

/**
 * A link with no target cannot navigate, so it is no link at all. A null `id` is **not** an error:
 * the contract sets it only for the `session` target, and `certification` and `access` arrive with
 * `id: null` because the portal has no per-row screen for them.
 */
function toLinkDto(link: PortalNotificationLinkDto | null | undefined): NotificationLinkDto | null {
  if (link === null || link === undefined) return null;
  const target = link.target ?? '';
  if (target === '') return null;
  return { target, ...(link.id === null || link.id === undefined ? {} : { id: link.id }) };
}

function toDocumentDto(
  document: PortalNotificationDocumentDto | null | undefined,
): NotificationDocumentDto | null {
  if (document === null || document === undefined) return null;
  return {
    id: text(document.id, 'notification.document.id'),
    name: text(document.name, 'notification.document.name'),
    url: text(document.url, 'notification.document.url'),
  };
}

/**
 * One row, from the generated shape to the one the adapter reads.
 *
 * `title` and `body` are deliberately dropped: they are the English render the backend wrote when
 * the row was created, kept on the wire for the dashboard's adapter, and this screen renders
 * `message` through its own EN/FR table instead.
 *
 * Exported for its unit tests. The alternative — reaching it only through a query — would make
 * every mapper case a fetch-mocking exercise, and `applyRead` above already sets the precedent
 * that a pure function in this module is tested directly.
 */
export function toItemDto(dto: PortalNotificationDto): NotificationItemDto {
  return {
    id: text(dto.id, 'notification.id'),
    kind: dto.kind,
    severity: dto.severity,
    delivery: dto.delivery,
    createdAt: dto.createdAt,
    readAt: dto.readAt ?? null,
    message: toMessageDto(dto.message),
    link: toLinkDto(dto.link),
    requiresAcknowledgement: dto.requiresAcknowledgement,
    acknowledgedAt: dto.acknowledgedAt ?? null,
    dismissedAt: dto.dismissedAt ?? null,
    document: toDocumentDto(dto.document),
  };
}

/**
 * One page. `items` is generated as nullable and the contract promises an empty array, so the
 * coalescing is a guard against the schema rather than against the server. `totalCount` and
 * `unreadCount` answer different questions — rows matching this query across all pages, and the
 * caller's unread total ignoring every filter — so neither is derived from the other here.
 */
export function toPageDto(dto: PortalNotificationFeedDto): NotificationPageDto {
  return {
    items: (dto.items ?? []).map(toItemDto),
    totalCount: dto.totalCount,
    nextCursor: dto.nextCursor ?? null,
    unreadCount: dto.unreadCount,
  };
}

/* ------------------------------------------------------------------ *
 * Reads
 * ------------------------------------------------------------------ */

/**
 * The feed's query string, built from nothing but the closed vocabulary `toNotificationFilters`
 * produces. `unreadOnly` is **deliberately never sent**: despite the name it is not a read-state
 * filter but the dashboard's modal-and-toast queue (`acknowledgedAt` and `dismissedAt` both null),
 * and read state lives on `status`. Sending both would silently ignore one of them.
 *
 * `limit` is stringified because the backend declares it as a string; it is an integer 1..50 that
 * is rejected rather than clamped when out of range, and {@link NOTIFICATIONS_PAGE_SIZE} is inside.
 */
function toFeedParams(
  filters: NotificationFilters,
  cursor: string | null,
): GetApiPortalMeNotificationsParams {
  return {
    ...(filters.status === undefined ? {} : { status: filters.status }),
    ...(filters.kind === undefined ? {} : { kind: filters.kind }),
    ...(filters.q === undefined ? {} : { q: filters.q }),
    ...(cursor === null ? {} : { cursor }),
    limit: String(NOTIFICATIONS_PAGE_SIZE),
  };
}

export function unreadCountQuery() {
  return queryOptions<number>({
    queryKey: notificationsKeys.unreadCount(),
    queryFn: async ({ signal }) => {
      try {
        return Math.max(0, (await getApiPortalMeNotificationsUnreadCount(undefined, signal)).count);
      } catch (error) {
        throw toApiError(error);
      }
    },
    staleTime: UNREAD_COUNT_STALE_MS,
    refetchOnWindowFocus: REFETCH_COUNT_ON_FOCUS,
  });
}

/**
 * The feed under one set of filters, loaded a page at a time. Each load also writes the unread
 * count, through the client the query function is handed — the rule that keeps the two badges
 * honest.
 */
export function notificationsQuery(filters: NotificationFilters) {
  return infiniteQueryOptions<
    NotificationPageView,
    Error,
    NotificationPages,
    ReturnType<typeof notificationsKeys.list>,
    string | null
  >({
    queryKey: notificationsKeys.list(filters),
    initialPageParam: null,
    queryFn: async ({ pageParam, client, signal }) => {
      try {
        const page = toNotificationPageView(
          toPageDto(
            await getApiPortalMeNotifications(toFeedParams(filters, pageParam), undefined, signal),
          ),
        );
        client.setQueryData(notificationsKeys.unreadCount(), page.unreadCount);
        return page;
      } catch (error) {
        throw toApiError(error);
      }
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor,
  });
}

/** Every loaded row across every page, in feed order. */
export function flattenPages(data: NotificationPages | undefined): NotificationView[] {
  return data?.pages.flatMap((page) => page.items) ?? [];
}

/**
 * The newest `createdAt` the user has loaded — `read-all`'s `upTo`, so a notification that arrives
 * after the click stays unread (contract §3). `null` when nothing is loaded.
 */
export function newestLoadedAt(data: NotificationPages | undefined): Date | null {
  let newest: Date | null = null;
  for (const item of flattenPages(data)) {
    if (newest === null || item.createdAt.getTime() > newest.getTime()) newest = item.createdAt;
  }
  return newest;
}

/* ------------------------------------------------------------------ *
 * Writes
 * ------------------------------------------------------------------ */

type ListSnapshot = [QueryKey, NotificationPages | undefined][];

export interface ReadContext {
  lists: ListSnapshot;
  count: number | undefined;
}

/** Every loaded page with `shouldRead` rows turned read. Pure, so the rollback test needs no client. */
export function applyRead(
  data: NotificationPages,
  shouldRead: (item: NotificationView) => boolean,
): NotificationPages {
  return {
    ...data,
    pages: data.pages.map((page) => ({
      ...page,
      items: page.items.map((item) =>
        item.unread && shouldRead(item) ? { ...item, unread: false } : item,
      ),
    })),
  };
}

async function snapshot(queryClient: QueryClient): Promise<ReadContext> {
  // A refetch landing mid-write would overwrite the optimistic rows with the stale ones.
  await queryClient.cancelQueries({ queryKey: notificationsKeys.root });
  return {
    lists: queryClient.getQueriesData<NotificationPages>({ queryKey: notificationsKeys.lists() }),
    count: queryClient.getQueryData<number>(notificationsKeys.unreadCount()),
  };
}

function restore(queryClient: QueryClient, context: ReadContext | undefined): void {
  if (context === undefined) return;
  for (const [key, data] of context.lists) queryClient.setQueryData(key, data);
  queryClient.setQueryData(notificationsKeys.unreadCount(), context.count);
}

/** What an app adds to a read: its own queries that read the same feed (the portal's dashboard). */
export interface ReadMutationOptions {
  invalidate?: readonly QueryKey[] | undefined;
}

async function refreshAll(
  queryClient: QueryClient,
  { invalidate = [] }: ReadMutationOptions,
): Promise<void> {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: notificationsKeys.root }),
    ...invalidate.map((queryKey) => queryClient.invalidateQueries({ queryKey })),
  ]);
}

function wasUnread(lists: ListSnapshot, id: string): boolean {
  return lists.some(([, data]) => flattenPages(data).some((item) => item.id === id && item.unread));
}

export function markReadMutation(
  queryClient: QueryClient,
  options: ReadMutationOptions = {},
): UseMutationOptions<NotificationView | null, Error, { id: string }, ReadContext> {
  return {
    // The one write on this controller that answers a single row rather than the feed, because the
    // screen updates the row it acted on. Idempotent: a second call returns the `readAt` the first
    // one wrote, since the record is of when the user actually read the message.
    mutationFn: async ({ id }) => {
      try {
        return toNotificationView(toItemDto(await postApiPortalMeNotificationsIdRead(id)));
      } catch (error) {
        throw toApiError(error);
      }
    },
    onMutate: async ({ id }) => {
      const context = await snapshot(queryClient);
      // Only a row that was unread moves the count; marking a read row again is a no-op on screen.
      const decrement = wasUnread(context.lists, id);
      queryClient.setQueriesData<NotificationPages>(
        { queryKey: notificationsKeys.lists() },
        (data) => (data === undefined ? data : applyRead(data, (item) => item.id === id)),
      );
      if (decrement && context.count !== undefined) {
        queryClient.setQueryData(notificationsKeys.unreadCount(), Math.max(0, context.count - 1));
      }
      return context;
    },
    onError: (_error, _input, context) => {
      restore(queryClient, context);
    },
    onSettled: async () => {
      await refreshAll(queryClient, options);
    },
  };
}

export function markAllReadMutation(
  queryClient: QueryClient,
  options: ReadMutationOptions = {},
): UseMutationOptions<number, Error, { upTo: Date }, ReadContext> {
  return {
    // `upTo` is the newest `createdAt` that was on screen, and the comparison is `createdAt <= upTo`
    // exactly — so a notification that arrived after the user looked stays unread. That is the whole
    // reason the field exists, and why this is not a bare "mark everything".
    mutationFn: async ({ upTo }) => {
      try {
        const answer = await postApiPortalMeNotificationsReadAll({ upTo: upTo.toISOString() });
        return Math.max(0, answer.unreadCount);
      } catch (error) {
        throw toApiError(error);
      }
    },
    onMutate: async ({ upTo }) => {
      const context = await snapshot(queryClient);
      queryClient.setQueriesData<NotificationPages>(
        { queryKey: notificationsKeys.lists() },
        (data) =>
          data === undefined
            ? data
            : applyRead(data, (item) => item.createdAt.getTime() <= upTo.getTime()),
      );
      queryClient.setQueryData(notificationsKeys.unreadCount(), 0);
      return context;
    },
    onError: (_error, _input, context) => {
      restore(queryClient, context);
    },
    // Anything that arrived after `upTo` is still unread, and only the server knows how much.
    onSuccess: (count) => {
      queryClient.setQueryData(notificationsKeys.unreadCount(), count);
    },
    onSettled: async () => {
      await refreshAll(queryClient, options);
    },
  };
}
